import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Center,
  Flex,
  Heading,
  Image,
  ListItem,
  Text,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import { FocusableElement } from '@chakra-ui/utils';
import { SignalingWarningTriangle } from 'assets/images';
import { useGlobalContext } from 'GlobalContext';
import React, { RefObject, useState } from 'react';
export interface AlertButtonOutput {
  clickedButton: AlertButtonConfig;
  close: () => void;
  atualizeStateButton: (button: AlertButtonConfig) => void;
}
export type AlertButtonVariant =
  | 'regular'
  | 'emphasisDanger'
  | 'emphasisSuccess';
export interface AlertButtonConfig {
  text: string;
  variant?: AlertButtonVariant;
  preventClose?: boolean;
  onClick?: (alertRender: AlertButtonOutput) => void;
  disabled?: boolean;
}
export interface AlertOpenConfig {
  title: string;
  message?: string;
  itensList?: string[];
  buttons?: AlertButtonConfig[];
}
export interface AlertController {
  showAlert: (props: AlertOpenConfig) => void;
}

const Alert = () => {
  const globalContext = useGlobalContext();
  const cancelRef = React.useRef<FocusableElement>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [title, setTitle] = useState<string>('');
  const [itensList, setItensList] = useState<string[]>();
  const [message, setMessage] = useState<string>('');
  const defaultButton: AlertButtonConfig = {
    text: 'Ok',
    variant: 'regular',
    onClick: () => {
      onClose();
    },
    disabled: false,
  };
  const [alertButtons, setAlertButtons] = useState<AlertButtonConfig[]>([
    defaultButton,
  ]);

  const setButtonDisabled = (
    newButtonState: AlertButtonConfig,
    buttonIndex: number,
  ) => {
    setAlertButtons((currentButtons) =>
      currentButtons.map((buttonItem, index) => {
        if (index === buttonIndex) {
          return newButtonState;
        }
        return buttonItem;
      }),
    );
  };
  const onClickAlertButton = (button: AlertButtonConfig, index: number) => {
    button.onClick?.({
      clickedButton: button,
      close: () => onClose(),
      atualizeStateButton: (button) => setButtonDisabled(button, index),
    });
    if (!button.preventClose) {
      onClose();
    }
  };

  const alertController: AlertController = {
    showAlert: (props: AlertOpenConfig) => {
      setTitle(props.title);
      setMessage(props.message || '');
      if (props.buttons?.length) {
        setAlertButtons(
          props.buttons.map((button) => {
            return { ...button, disabled: button.disabled ?? false };
          }),
        );
      } else {
        setAlertButtons([{ ...defaultButton, disabled: false }]);
      }
      if (props.itensList?.length) {
        setItensList(props.itensList);
      } else {
        setItensList(undefined);
      }
      onOpen();
    },
  };
  globalContext.alertController = alertController;
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef as RefObject<FocusableElement | null>}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent borderRadius={'20px'}>
          <Center pt="16" pb="4">
            <Image w="117px" src={SignalingWarningTriangle} />
          </Center>
          <AlertDialogHeader
            as={Heading}
            variant="large"
            textAlign={'center'}
            fontSize="lg"
            fontWeight="bold"
          >
            {title}
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody justifyContent={'center'}>
            <Text size="md" textAlign="center" color="yGray.500">
              {message}
            </Text>
            {itensList && (
              <UnorderedList>
                <Flex flexDir="column" alignItems={'center'} gap="1" mt="3">
                  {itensList.map((item, index) => (
                    <ListItem
                      key={index}
                      textAlign="center"
                      color="yGray.500"
                      maxW="90%"
                    >
                      {item}
                    </ListItem>
                  ))}
                </Flex>
              </UnorderedList>
            )}
          </AlertDialogBody>

          <AlertDialogFooter
            paddingInlineEnd={'6'}
            paddingInlineStart={'6'}
            paddingBlockEnd={'6'}
          >
            <Flex flexDir="column" alignItems="center" w="100%" gap="4">
              {alertButtons.map((button, index) => (
                <Button
                  key={index}
                  variant={button.variant}
                  size="large"
                  width={'100%'}
                  flex="1"
                  // Usar a propriedade disabled do botão
                  isDisabled={button.disabled}
                  onClick={() => {
                    onClickAlertButton(button, index);
                  }}
                >
                  {button.text}
                </Button>
              ))}
            </Flex>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
export default Alert;
